// Theme choice in the profile: a picture of the current theme, a "Change" button that opens every
// theme to pick from, and light / dark / follow-the-device.
import { useState, type CSSProperties } from 'react';
import { t } from '../i18n/index.ts';
import { Dialog, useToast } from './ui.tsx';
import { useThemeSetting } from '../lib/theme.ts';
import { findTheme, THEMES, type ColorMode, type Theme } from '../../shared/themes.ts';

function useResolvedMode(mode: ColorMode): 'light' | 'dark' {
  if (mode !== 'system') return mode;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** A small drawing of the app in the given theme: page, a panel, the table and the accents. */
export function ThemePreview({ theme, mode }: { theme: Theme; mode: 'light' | 'dark' }) {
  const p = theme[mode];
  const r = theme.radius;
  const style = {
    '--tp-frost': p.frost,
    '--tp-paper': p.paper,
    '--tp-line': p.line,
    '--tp-ink': p.ink,
    '--tp-ink-3': p['ink-3'],
    '--tp-felt': p.felt,
    '--tp-felt-2': p['felt-2'],
    '--tp-felt-deep': p['felt-deep'],
    '--tp-walnut': p.walnut,
    '--tp-walnut-2': p['walnut-2'],
    '--tp-brass': p.brass,
    '--tp-primary': p.primary,
    '--tp-primary-ink': p['primary-ink'],
    '--tp-h': p['suit-h'],
    '--tp-s': p['suit-s'],
    '--tp-d': p['suit-d'],
    '--tp-c': p['suit-c'],
    '--tp-r': `${r}px`,
  } as CSSProperties;
  return (
    <div className="theme-preview" style={style} aria-hidden="true">
      <div className="tp-bar">
        <span className="tp-dot" />
        <span className="tp-line" />
      </div>
      <div className="tp-table">
        <div className="tp-rail" />
        <div className="tp-felt">
          <span className="tp-card" style={{ background: 'var(--tp-h)' }} />
          <span className="tp-card" style={{ background: 'var(--tp-s)' }} />
          <span className="tp-card" style={{ background: 'var(--tp-d)' }} />
          <span className="tp-card" style={{ background: 'var(--tp-c)' }} />
        </div>
        <span className="tp-seat tp-seat-a" />
        <span className="tp-seat tp-seat-b" />
      </div>
      <div className="tp-foot">
        <span className="tp-chip" />
        <span className="tp-btn" />
      </div>
    </div>
  );
}

export function ThemeSettings() {
  const toast = useToast();
  const { theme, mode, setTheme, setMode } = useThemeSetting();
  const [open, setOpen] = useState(false);
  const resolved = useResolvedMode(mode);
  const current = findTheme(theme);
  const modes: [ColorMode, string][] = [
    ['system', t('theme.modeSystem')],
    ['light', t('theme.modeLight')],
    ['dark', t('theme.modeDark')],
  ];
  return (
    <div className="theme-settings">
      <span className="label">{t('theme.title')}</span>
      <div className="theme-current">
        <ThemePreview theme={current} mode={resolved} />
        <div className="theme-current-text">
          <strong>{current.name}</strong>
          <span className="hint">{current.note}</span>
          <button type="button" className="btn btn-small" aria-label={t('theme.changeLabel')} onClick={() => setOpen(true)}>
            {t('theme.change')}
          </button>
        </div>
      </div>
      <div className="seg-control" role="radiogroup" aria-label={t('theme.modeLabel')}>
        {modes.map(([m, label]) => (
          <button key={m} type="button" role="radio" aria-checked={mode === m} className={mode === m ? 'on' : ''} onClick={() => void setMode(m)}>
            {label}
          </button>
        ))}
      </div>
      {open && (
        <Dialog title={t('theme.dialogTitle')} onClose={() => setOpen(false)}>
          <div className="theme-grid" role="radiogroup" aria-label={t('theme.dialogTitle')}>
            {THEMES.map((th) => (
              <button
                key={th.id}
                type="button"
                role="radio"
                aria-checked={th.id === current.id}
                className={`theme-option${th.id === current.id ? ' selected' : ''}`}
                onClick={() => {
                  void setTheme(th.id);
                  setOpen(false);
                  toast(t('theme.changed', { name: th.name }));
                }}
              >
                <ThemePreview theme={th} mode={resolved} />
                <span className="theme-option-name">{th.name}</span>
              </button>
            ))}
          </div>
        </Dialog>
      )}
    </div>
  );
}
